// ============================================================================
// Expediente documental — metadatos visuales compartidos (tonos, iconos,
// formatos). Sin React: lo usan el checklist, la app Documentación y las
// pestañas de envíos/reservas.
// ============================================================================
import type { Tone } from "../../components/ui";
import type { DocumentCategory, DocumentDirection, DocumentMode, DocumentProvider, DocumentStatus, SubjectKind } from "../../utils/documentTypes";
import type { ShipmentDocument, SubjectState } from "../../services/shipmentDocuments";

/** Namespace i18n del expediente (lo registra i18n/index.ts). */
export const DOCS_NS = "documentacion";

export const STATUS_TONES: Record<DocumentStatus, Tone> = {
  requerido: "error",
  pendiente: "neutral",
  recibido: "info",
  en_revision: "warning",
  aprobado: "success",
  rechazado: "error",
  vencido: "error",
  generado: "success",
};

export const STATUS_ICONS: Record<DocumentStatus, string> = {
  requerido: "ri-upload-2-line",
  pendiente: "ri-time-line",
  recibido: "ri-inbox-archive-line",
  en_revision: "ri-search-eye-line",
  aprobado: "ri-checkbox-circle-line",
  rechazado: "ri-close-circle-line",
  vencido: "ri-calendar-close-line",
  generado: "ri-magic-line",
};

export const STATE_TONES: Record<SubjectState, Tone> = {
  completo: "success",
  incompleto: "warning",
  por_vencer: "warning",
  vencido: "error",
  sin_expediente: "neutral",
};

export const STATE_ICONS: Record<SubjectState, string> = {
  completo: "ri-checkbox-circle-line",
  incompleto: "ri-error-warning-line",
  por_vencer: "ri-time-line",
  vencido: "ri-calendar-close-line",
  sin_expediente: "ri-file-add-line",
};

export const PROVIDER_ICONS: Record<DocumentProvider, string> = {
  cliente: "ri-user-3-line",
  forwarder: "ri-ship-2-line",
  transportista: "ri-truck-line",
  aduana: "ri-government-line",
  aseguradora: "ri-shield-check-line",
};

export const MODE_ICONS: Record<DocumentMode, string> = {
  aereo: "ri-plane-line",
  maritimo: "ri-ship-line",
  terrestre: "ri-truck-line",
  multimodal: "ri-route-line",
};

export const DIRECTION_ICONS: Record<DocumentDirection, string> = {
  importacion: "ri-login-box-line",
  exportacion: "ri-logout-box-r-line",
  nacional: "ri-map-pin-line",
};

export const CATEGORY_ICONS: Record<DocumentCategory, string> = {
  comercial: "ri-bill-line",
  transporte: "ri-truck-line",
  aduanal: "ri-government-line",
  seguro: "ri-shield-check-line",
  sanitario: "ri-leaf-line",
  interno: "ri-folder-line",
};

export const KIND_ICONS: Record<SubjectKind, string> = {
  shipment: "ri-truck-line",
  booking: "ri-ship-2-line",
};

export const CATEGORY_ORDER: DocumentCategory[] = ["comercial", "transporte", "aduanal", "seguro", "sanitario", "interno"];

/** Tono por % de completitud. */
export function completenessTone(pct: number | null | undefined): Tone {
  if (pct === null || pct === undefined) return "neutral";
  if (pct >= 100) return "success";
  if (pct >= 60) return "warning";
  return "error";
}

/** Tono de un vencimiento en días (negativo = ya venció). */
export function expiryTone(days: number | null | undefined): Tone {
  if (days === null || days === undefined) return "neutral";
  if (days <= 0) return "error";
  if (days <= 7) return "error";
  if (days <= 30) return "warning";
  return "success";
}

/** Tamaño legible de un archivo. */
export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Fecha corta en el idioma activo. */
export function shortDate(value: string | null | undefined, lng: string | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(String(lng || "es").startsWith("en") ? "en-US" : "es-MX", { timeZone: "America/Mexico_City", day: "2-digit", month: "short", year: "numeric" });
}

/** Fecha y hora cortas en el idioma activo. */
export function shortDateTime(value: string | null | undefined, lng: string | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(String(lng || "es").startsWith("en") ? "en-US" : "es-MX", { timeZone: "America/Mexico_City", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** Valor para `<input type="date">` a partir de una ISO. */
export function toDateInput(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

/** El próximo vencimiento (en días) de un conjunto de documentos, o null. */
export function nextExpiry(docs: ShipmentDocument[]): { doc: ShipmentDocument; days: number } | null {
  let best: { doc: ShipmentDocument; days: number } | null = null;
  for (const d of docs) {
    if (d.expires_in_days === null || d.effective_status === "rechazado") continue;
    if (!best || d.expires_in_days < best.days) best = { doc: d, days: d.expires_in_days };
  }
  return best;
}

/** Agrupa los documentos por categoría en el orden del catálogo. */
export function groupByCategory(docs: ShipmentDocument[]): Array<{ category: DocumentCategory; documents: ShipmentDocument[] }> {
  const map = new Map<DocumentCategory, ShipmentDocument[]>();
  for (const d of docs) {
    const c = (d.category ?? "interno") as DocumentCategory;
    if (!map.has(c)) map.set(c, []);
    map.get(c)!.push(d);
  }
  return CATEGORY_ORDER.filter((c) => map.has(c)).map((c) => ({ category: c, documents: map.get(c)! }));
}

export const ACCEPTED_MIME = ["application/pdf", "image/jpeg", "image/png", "application/xml", "text/xml"];
export const ACCEPT_ATTR = ".pdf,.jpg,.jpeg,.png,.xml,application/pdf,image/jpeg,image/png,application/xml,text/xml";
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

/** Valida un archivo antes de subirlo: tipo y tamaño (mismas reglas que el API). */
export function validateUploadFile(file: File): "ok" | "type" | "size" {
  const mime = (file.type || "").toLowerCase();
  const byExt = /\.(pdf|jpe?g|png|xml)$/i.test(file.name);
  if (!(ACCEPTED_MIME.includes(mime) || (!mime && byExt))) return "type";
  if (file.size > MAX_FILE_BYTES) return "size";
  return "ok";
}
